import { useEffect, useState, createContext, useContext, useMemo, useCallback } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ShoppingCart, X, Bell, Trash2, Plus, Minus, Lock, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import PaymentMethods from "./PaymentMethods";

/**
 * Ashvolt Cart — dynamic client-side cart.
 * - Adds / removes / updates qty of items.
 * - Persists to localStorage under `ashvolt.cart.v1`.
 * - Preorders are locked until 2027 → checkout button shows a locked message.
 * - Emits sonner toasts on add / remove.
 */

const STORAGE_KEY = "ashvolt.cart.v1";

function loadInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function parsePriceToNumber(price) {
  if (typeof price === "number") return price;
  if (!price) return 0;
  // e.g. "14,99€" -> 14.99
  const cleaned = String(price).replace(/[^0-9,\.]/g, "").replace(",", ".");
  const n = parseFloat(cleaned);
  return Number.isFinite(n) ? n : 0;
}

function formatEuro(n) {
  return `${n.toFixed(2).replace(".", ",")}€`;
}

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState(loadInitial);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch {
      /* quota errors → ignore */
    }
  }, [items]);

  const addItem = useCallback(
    (item) => {
      // item: { id, name, price, image?, variant?, meta? }
      setItems((prev) => {
        const key = `${item.id}${item.variant ? "::" + item.variant : ""}`;
        const existing = prev.find((p) => p.key === key);
        if (existing) {
          return prev.map((p) =>
            p.key === key ? { ...p, qty: p.qty + 1 } : p
          );
        }
        return [
          ...prev,
          {
            key,
            id: item.id,
            name: item.name,
            price: item.price,
            image: item.image || null,
            variant: item.variant || null,
            meta: item.meta || null,
            qty: 1,
          },
        ];
      });
      toast.success(`${item.name} aggiunto al carrello`, {
        description: item.variant
          ? `Variante: ${item.variant} · in attesa dei preordini`
          : "In attesa dell'apertura dei preordini",
      });
      setOpen(true);
    },
    []
  );

  const removeItem = useCallback((key) => {
    setItems((prev) => prev.filter((p) => p.key !== key));
    toast("Articolo rimosso dal carrello");
  }, []);

  const updateQty = useCallback((key, delta) => {
    setItems((prev) =>
      prev
        .map((p) => (p.key === key ? { ...p, qty: p.qty + delta } : p))
        .filter((p) => p.qty > 0)
    );
  }, []);

  const clear = useCallback(() => {
    setItems([]);
    toast("Carrello svuotato");
  }, []);

  const totals = useMemo(() => {
    const count = items.reduce((s, i) => s + i.qty, 0);
    const subtotal = items.reduce(
      (s, i) => s + parsePriceToNumber(i.price) * i.qty,
      0
    );
    return { count, subtotal };
  }, [items]);

  const value = useMemo(
    () => ({
      open,
      setOpen,
      items,
      addItem,
      removeItem,
      updateQty,
      clear,
      count: totals.count,
      subtotal: totals.subtotal,
    }),
    [open, items, addItem, removeItem, updateQty, clear, totals]
  );

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}

export function CartButton({ className = "" }) {
  const { setOpen, count } = useCart();
  return (
    <button
      type="button"
      data-testid="cart-open-btn"
      onClick={() => setOpen(true)}
      className={`relative flex items-center gap-2 border border-white/15 hover:border-accent/50 px-3 py-2 backdrop-blur-md bg-white/[0.02] transition-colors group ${className}`}
      aria-label="Apri il carrello"
    >
      <ShoppingCart
        size={16}
        className="text-white/80 group-hover:text-accent transition-colors"
      />
      <span className="hidden md:inline font-body text-[10px] tracking-[0.3em] uppercase text-white/70 group-hover:text-white transition-colors">
        Carrello
      </span>
      <motion.span
        key={count}
        initial={{ scale: 0.6, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: "spring", stiffness: 500, damping: 18 }}
        data-testid="cart-badge"
        className={`absolute -top-1.5 -right-1.5 flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-body font-bold rounded-full ${
          count > 0 ? "bg-accent text-black" : "bg-white/20 text-white/60"
        }`}
      >
        {count}
      </motion.span>
    </button>
  );
}

export function CartDrawer() {
  const { open, setOpen, items, removeItem, updateQty, clear, subtotal, count } =
    useCart();

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, setOpen]);

  const hasItems = items.length > 0;

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="cart-drawer-backdrop"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[300] bg-black/80 backdrop-blur-md"
          onClick={() => setOpen(false)}
        >
          <motion.aside
            data-testid="cart-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0 right-0 h-full w-full max-w-md bg-[#1A1A1F] border-l border-white/10 flex flex-col"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 md:px-8 py-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <ShoppingCart size={16} className="text-accent" />
                <span className="font-heading text-xl uppercase tracking-[0.15em] text-white">
                  Carrello
                </span>
                {hasItems && (
                  <span
                    data-testid="cart-drawer-count"
                    className="font-body text-[10px] uppercase tracking-[0.25em] text-white/50 border border-white/15 px-2 py-0.5"
                  >
                    {count} articol{count === 1 ? "o" : "i"}
                  </span>
                )}
              </div>
              <button
                type="button"
                data-testid="cart-close-btn"
                onClick={() => setOpen(false)}
                className="p-2 text-white/60 hover:text-accent transition-colors"
                aria-label="Chiudi carrello"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            {!hasItems ? (
              <div className="flex-1 flex flex-col justify-center px-6 md:px-8 py-10 text-center">
                <div className="mx-auto w-16 h-16 rounded-full border border-white/10 flex items-center justify-center mb-6">
                  <ShoppingCart size={22} className="text-white/40" />
                </div>
                <div className="font-heading uppercase text-white text-3xl md:text-4xl leading-[0.95] mb-4">
                  Il tuo carrello<br />è vuoto.
                </div>
                <p className="font-body text-sm md:text-base text-white/60 leading-relaxed max-w-sm mx-auto mb-8">
                  Aggiungi edizioni del gioco o gadget: rimarranno salvate qui e potrai
                  finalizzare il preordine all'apertura del{" "}
                  <span className="text-white">20 maggio 2027</span>.
                </p>
                <div className="flex flex-col gap-3 max-w-xs mx-auto w-full">
                  <a
                    href="/store"
                    data-testid="cart-visit-store"
                    onClick={() => setOpen(false)}
                    className="inline-flex items-center justify-center gap-3 border border-accent/40 bg-accent/[0.06] hover:bg-accent/[0.1] px-5 py-3 transition-colors"
                  >
                    <Bell size={13} className="text-accent" />
                    <span className="font-heading text-sm tracking-[0.15em] uppercase text-accent">
                      Vai allo store
                    </span>
                  </a>
                </div>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                <ul
                  data-testid="cart-items-list"
                  className="flex-1 overflow-y-auto divide-y divide-white/10"
                >
                  {items.map((item) => (
                    <li
                      key={item.key}
                      data-testid={`cart-item-${item.key}`}
                      className="flex gap-4 px-6 md:px-8 py-5"
                    >
                      <div className="w-16 h-16 shrink-0 border border-white/10 bg-[#0F0F12] overflow-hidden flex items-center justify-center">
                        {item.image ? (
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <ShoppingBag size={20} className="text-white/30" />
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-heading uppercase text-white text-base leading-tight truncate">
                          {item.name}
                        </div>
                        {item.variant && (
                          <div className="font-body text-[10px] uppercase tracking-[0.25em] text-white/45 mt-1">
                            {item.variant}
                          </div>
                        )}
                        <div className="flex items-center justify-between mt-3">
                          <div className="flex items-center border border-white/15">
                            <button
                              type="button"
                              data-testid={`cart-qty-dec-${item.key}`}
                              onClick={() => updateQty(item.key, -1)}
                              aria-label="Diminuisci quantità"
                              className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-accent hover:bg-white/[0.03] transition-colors"
                            >
                              <Minus size={12} />
                            </button>
                            <span className="w-8 text-center font-body text-sm text-white">
                              {item.qty}
                            </span>
                            <button
                              type="button"
                              data-testid={`cart-qty-inc-${item.key}`}
                              onClick={() => updateQty(item.key, 1)}
                              aria-label="Aumenta quantità"
                              className="w-7 h-7 flex items-center justify-center text-white/70 hover:text-accent hover:bg-white/[0.03] transition-colors"
                            >
                              <Plus size={12} />
                            </button>
                          </div>
                          <div className="font-heading text-lg text-accent leading-none tabular-nums">
                            {formatEuro(parsePriceToNumber(item.price) * item.qty)}
                          </div>
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid={`cart-remove-${item.key}`}
                        onClick={() => removeItem(item.key)}
                        className="self-start p-1.5 text-white/40 hover:text-red-400 transition-colors"
                        aria-label={`Rimuovi ${item.name}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </li>
                  ))}
                </ul>

                {/* Totals + locked checkout */}
                <div className="border-t border-white/10 px-6 md:px-8 py-5 bg-[#0F0F12]/60">
                  <div className="flex items-center justify-between mb-4">
                    <span className="font-body text-[11px] uppercase tracking-[0.3em] text-white/60">
                      Subtotale
                    </span>
                    <span
                      data-testid="cart-subtotal"
                      className="font-heading text-2xl text-white tabular-nums"
                    >
                      {formatEuro(subtotal)}
                    </span>
                  </div>
                  <button
                    type="button"
                    data-testid="cart-checkout-btn"
                    disabled
                    className="w-full inline-flex items-center justify-center gap-3 border border-accent/30 bg-accent/[0.04] px-5 py-3.5 cursor-not-allowed"
                    aria-disabled="true"
                    title="I preordini apriranno il 20 maggio 2027"
                  >
                    <Lock size={13} className="text-accent" />
                    <span className="font-heading text-sm tracking-[0.15em] uppercase text-accent">
                      Checkout — 20 maggio 2027
                    </span>
                  </button>
                  <button
                    type="button"
                    data-testid="cart-clear-btn"
                    onClick={clear}
                    className="w-full mt-3 font-body text-[10px] uppercase tracking-[0.3em] text-white/40 hover:text-red-300 transition-colors"
                  >
                    Svuota carrello
                  </button>
                </div>
              </div>
            )}

            {/* Payments footer */}
            <div className="border-t border-white/10 px-6 md:px-8 py-6">
              <PaymentMethods compact />
              <p className="font-body text-[10px] uppercase tracking-[0.25em] text-white/40 mt-4 leading-relaxed">
                Al checkout potrai pagare con Apple Pay, Visa, Mastercard o PayPal.
              </p>
            </div>
          </motion.aside>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
